import React, { useState, useEffect, useRef } from "react";
import TopMenuBar from "../TopMenuBar";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Container from "@mui/material/Container";
import { Button } from "@mui/material";
import { useSetAdminConsent } from "../../services/useSetAdminConsent";
import { Helmet } from "react-helmet";
import { AdminConsentModel } from "../../Models/AdminConsentModel";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AdminConsentLanding: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const query = useQuery();
  const adminConsent = query.get("admin_consent");
  const tenant = query.get("tenant");
  const [tenantStored, setTenantStored] = useState<Boolean>(false);
  const [issueStoring, setIssueStoring] = useState<Boolean>(false);
  const tenantSet = useRef(false);

  function getUrl(): string {
    if (typeof window == "undefined") return "";
    return window.location.href;
  }

  let metaTitle = "ChangePilot Admin Consent";
  let metaDescription = `This page is shown to users returning from the Microsoft 365 Admin Consent process. Connected organisations see all messages specifically for their environment.`;
  const fallBackLogo =
    window.location.protocol +
    "//" +
    window.location.hostname +
    "/ChangePilotFaviconMint500px.png";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: metaTitle,
    alternateName: "ChangePilot from Empowering Cloud",
    url: getUrl(),
    image: fallBackLogo,
  };

  const setAdminConsent = useSetAdminConsent();

  useEffect(() => {
    const model: AdminConsentModel = {
      tenantId: tenant ? tenant : "",
    };

    const setTenantInfo = async () => {
      if (isLoading) {
        console.log("Auth0 not ready");
        return;
      }

      if (tenantSet.current) {
        return;
      }
      tenantSet.current = true;

      const consentResult = await setAdminConsent(model);

      if (consentResult === undefined) {
        return;
      }

      if (consentResult) {
        setTenantStored(true);
      } else {
        setIssueStoring(true);
      }
    };
    setTenantInfo();
  }, [setAdminConsent, tenant, isLoading]);

  if (isLoading) {
    return (
      <>
        <div>Loading ...</div>
        <Helmet>
          <title>{metaTitle}</title>
          {metaDescription && (
            <meta name="description" content={metaDescription} />
          )}
          {metaTitle && <meta property="og:title" content={metaTitle} />}
          {metaDescription && (
            <meta property="og:description" content={metaDescription} />
          )}
          <meta property="og:type" content="website"></meta>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          {getUrl() && <meta property="og:url" content={getUrl()} />}
          <meta property="og:image" content={fallBackLogo} />
          <link rel="canonical" href={getUrl()} />
        </Helmet>
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>{metaTitle}</title>
          {metaDescription && (
            <meta name="description" content={metaDescription} />
          )}
          {metaTitle && <meta property="og:title" content={metaTitle} />}
          {metaDescription && (
            <meta property="og:description" content={metaDescription} />
          )}
          <meta property="og:type" content="website"></meta>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          {getUrl() && <meta property="og:url" content={getUrl()} />}
          <meta property="og:image" content={fallBackLogo} />
          <link rel="canonical" href={getUrl()} />
        </Helmet>
        <TopMenuBar />
        <Container maxWidth="xl">
          {adminConsent === "True" && tenantStored && (
            <>
              <h1>Tenant Connection Successful</h1>
              <p>
                You have successfully connected ChangePilot to your Microsoft
                365 tenant. The ChangePilot Message Center feed and your
                ChangePilot Emails will now mirror your organisation's specific
                Message Center items. This applies to all ChangePilot users
                verified as part of your organisation.{" "}
              </p>
              <Button href="/settings/tenant" variant="contained">
                Continue
              </Button>
            </>
          )}

          {adminConsent === "True" && !tenantStored && !issueStoring && (
            <>
              <Helmet>
                <title>{metaTitle}</title>
                {metaDescription && (
                  <meta name="description" content={metaDescription} />
                )}
                {metaTitle && <meta property="og:title" content={metaTitle} />}
                {metaDescription && (
                  <meta property="og:description" content={metaDescription} />
                )}
                <meta property="og:type" content="website"></meta>
                <script type="application/ld+json">
                  {JSON.stringify(jsonLd)}
                </script>
                {getUrl() && <meta property="og:url" content={getUrl()} />}
                <meta property="og:image" content={fallBackLogo} />
                <link rel="canonical" href={getUrl()} />
              </Helmet>
              <h1>Processing Consent...</h1>
              <p>Please wait whilst we process your consent.</p>
            </>
          )}

          {(adminConsent === "False" || adminConsent == null) && (
            <>
              <Helmet>
                <title>{metaTitle}</title>
                {metaDescription && (
                  <meta name="description" content={metaDescription} />
                )}
                {metaTitle && <meta property="og:title" content={metaTitle} />}
                {metaDescription && (
                  <meta property="og:description" content={metaDescription} />
                )}
                <meta property="og:type" content="website"></meta>
                <script type="application/ld+json">
                  {JSON.stringify(jsonLd)}
                </script>
                {getUrl() && <meta property="og:url" content={getUrl()} />}
                <meta property="og:image" content={fallBackLogo} />
                <link rel="canonical" href={getUrl()} />
              </Helmet>
              <h1>Consent Failed</h1>
              <p>
                You have not consented to the ChangePilot application, or
                something went wrong. Please try again.
              </p>
            </>
          )}

          {issueStoring && (
            <>
              <Helmet>
                <title>{metaTitle}</title>
                {metaDescription && (
                  <meta name="description" content={metaDescription} />
                )}
                {metaTitle && <meta property="og:title" content={metaTitle} />}
                {metaDescription && (
                  <meta property="og:description" content={metaDescription} />
                )}
                <meta property="og:type" content="website"></meta>
                <script type="application/ld+json">
                  {JSON.stringify(jsonLd)}
                </script>
                {getUrl() && <meta property="og:url" content={getUrl()} />}
                <meta property="og:image" content={fallBackLogo} />
                <link rel="canonical" href={getUrl()} />
              </Helmet>
              <h1>Consent Failed</h1>
              <p>
                Something went wrong storing the tenant information. Please try
                again.
              </p>
            </>
          )}
        </Container>
      </>
    );
  } else {
    return (
      <>
        <Helmet>
          <title>{metaTitle}</title>
          {metaDescription && (
            <meta name="description" content={metaDescription} />
          )}
          {metaTitle && <meta property="og:title" content={metaTitle} />}
          {metaDescription && (
            <meta property="og:description" content={metaDescription} />
          )}
          <meta property="og:type" content="website"></meta>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          {getUrl() && <meta property="og:url" content={getUrl()} />}
          <meta property="og:image" content={fallBackLogo} />
          <link rel="canonical" href={getUrl()} />
        </Helmet>
        <p>
          Something went wrong, or you are trying to consent without being
          logged in. Please log in and try again.
        </p>
      </>
    );
  }
};

export default AdminConsentLanding;
